import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSession, type PartyType } from '@/lib/auth/session';
import {
  widgetByKey,
  widgetsFor,
  type WidgetDefinition,
  type WidgetSize,
} from '@/pages/home/widgets/registry';

/**
 * 홈 위젯을 <b>사람이 고른 대로</b> 놓는다 — 켜고 끄기 · 순서 · 크기.
 *
 * <h2>왜 필요한가</h2>
 * 위젯이 열여섯이다. 협력사마다 매일 보는 것이 다르다 — 골조는 검측·출역을 보고, 설비는
 * 자재 승인·구매를 본다. 전부 같은 순서로 깔아 두면 <b>자기에게 필요한 칸이 스크롤 아래로</b> 간다.
 *
 * <h2>서버에 두지 않는다</h2>
 * front 는 서버에 저장하고 오프라인일 때만 localStorage 로 물러선다. 여기는 <b>localStorage 뿐</b>이다 —
 * 위젯 배치는 기기의 취향이지 계정의 자료가 아니고(사무실 큰 화면과 현장 휴대폰의 답이 다르다),
 * 그것 하나 때문에 표와 API 를 만들면 포털이 무거워진다. 필요해지면 그때 서버로 올린다.
 *
 * <h2>저장 모양은 front 와 같다</h2>
 * {@code slots: [{ widgetKey, size, visible }]}. 예전 모양({@code { order, hidden }})은 읽지 않고
 * <b>버린다</b> — 모르는 모양은 없는 것으로 본다(DOC-84 §3-A). 변환기를 두면 그 코드가 영원히 남는다.
 *
 * <h2>계정별로 나눈다</h2>
 * 한 기기를 협력사와 감리가 번갈아 쓰는 일이 있다(현장 사무실 PC). 키에 사용자 id 를 넣어
 * 서로의 배치를 덮지 않게 한다.
 *
 * <h2>저장된 것과 카탈로그가 어긋날 때</h2>
 * 위젯은 늘어나고 주체는 바뀐다. 저장된 배치를 그대로 믿지 않고 매번 카탈로그와 맞춘다({@link mergeSlots}).
 *  - 카탈로그에 없는 키(위젯이 없어졌거나 이 주체의 것이 아님)는 버린다
 *  - 카탈로그에 있는데 저장에 없는 키(새로 생긴 위젯)는 기본값으로 뒤에 붙인다 — 새 기능이 조용히 감춰지면 아무도 모른다
 *  - 고정 위젯은 저장된 값이 무엇이든 항상 보이고, 항상 앞에 온다
 */

const STORAGE_PREFIX = 'bomi.vendor.widgets';

export type WidgetSlot = {
  widgetKey: string;
  size: WidgetSize;
  visible: boolean;
};

export type HomeWidgets = {
  /** 이 계정이 쓸 수 있는 위젯 전부(꺼진 것 포함, 순서대로). 위젯 관리 모달이 편집하는 대상이다. */
  slots: WidgetSlot[];
  /** 홈에 실제로 그릴 것. 순서대로다. */
  visible: Array<{ definition: WidgetDefinition; size: WidgetSize }>;
  save: (next: WidgetSlot[]) => void;
  /** 기본 배치로 되돌린다. 저장해 둔 것을 지운다. */
  reset: () => void;
  /** 하나라도 손댄 적이 있는가. 관리 화면이 「되돌리기」 버튼을 세울지 정한다. */
  customized: boolean;
};

export function useHomeWidgets(partyType: PartyType): HomeWidgets {
  const session = useSession();
  const userId = session.data?.id ?? null;

  const [stored, setStored] = useState<WidgetSlot[] | null>(null);

  // 계정이 늦게 도착한다(세션 조회). 그때 다시 읽는다 — 안 그러면 다른 사람 배치를 보게 된다.
  useEffect(() => {
    setStored(userId === null ? null : read(userId));
  }, [userId]);

  /*
    업체의 갈래로 카탈로그를 거른다(V348 · V364). 세션이 아직이면 안 거른다.

    ★ 의존성은 <b>세션이 준 배열 그대로</b>다. 여기서 `?? []` 를 미리 풀면 매 렌더 새 배열이 되어
      카탈로그가 통째로 다시 계산되고, 그 뒤의 `mergeSlots` 까지 줄줄이 따라간다.
  */
  const kinds = session.data?.vendorKinds;
  const available = useMemo(() => widgetsFor(partyType, kinds ?? []), [partyType, kinds]);

  const slots = useMemo(() => mergeSlots(stored, available), [stored, available]);

  const save = useCallback(
    (next: WidgetSlot[]) => {
      const merged = mergeSlots(next, available);
      setStored(merged);
      if (userId !== null) write(userId, merged);
    },
    [available, userId],
  );

  const reset = useCallback(() => {
    setStored(null);
    if (userId !== null) clear(userId);
  }, [userId]);

  const visible = useMemo(
    () =>
      slots
        .filter((slot) => slot.visible)
        .map((slot) => ({ definition: widgetByKey(slot.widgetKey), size: slot.size }))
        .filter((entry): entry is { definition: WidgetDefinition; size: WidgetSize } => entry.definition !== null),
    [slots],
  );

  return { slots, visible, save, reset, customized: stored !== null };
}

/**
 * 저장된 배치를 카탈로그와 맞춘다. front 의 같은 이름 함수와 같은 규칙이다(조건 고정만 없다).
 *
 * <p>내보내는 이유는 테스트 때문이다 — 「모르는 키를 버린다 · 새 키를 뒤에 붙인다 · 크기를 지킨다」가
 * 훅을 띄우지 않고도 확인돼야 한다.
 */
export function mergeSlots(stored: WidgetSlot[] | null, available: WidgetDefinition[]): WidgetSlot[] {
  const availableKeys = new Set(available.map((definition) => definition.widgetKey));

  const kept = (stored ?? []).filter((slot) => availableKeys.has(slot.widgetKey));
  const keptKeys = new Set(kept.map((slot) => slot.widgetKey));

  const added = available
    .filter((definition) => !keptKeys.has(definition.widgetKey))
    .map((definition) => ({
      widgetKey: definition.widgetKey,
      size: definition.defaultSize,
      visible: definition.defaultVisible,
    }));

  return [...kept, ...added]
    .map((slot) => {
      const definition = widgetByKey(slot.widgetKey);
      if (!definition) return slot;
      return {
        widgetKey: slot.widgetKey,
        // 정의에서 사라진 크기가 저장돼 있으면(예: wide 를 없앤 위젯) 기본 크기로 되돌린다.
        size: definition.sizes.includes(slot.size) ? slot.size : definition.defaultSize,
        visible: definition.fixed ? true : slot.visible,
      };
    })
    // 고정 위젯이 앞. 같은 무리 안에서는 저장된 순서를 지킨다(정렬이 안정적이다).
    .sort((a, b) => rankOf(a.widgetKey) - rankOf(b.widgetKey));
}

function rankOf(widgetKey: string): number {
  return widgetByKey(widgetKey)?.fixed ? 0 : 1;
}

// ── 저장소 ──────────────────────────────────────────────────────────────────

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}.${userId}`;
}

function read(userId: string): WidgetSlot[] | null {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    // 배열이 아니면 옛 모양({ order, hidden })이거나 손상된 값이다. 둘 다 없는 것으로 본다.
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(isSlot);
  }
  catch {
    // 손상된 값은 없는 것으로 본다 — 기본 배치로 열리는 편이 빈 화면보다 낫다.
    return null;
  }
}

function write(userId: string, value: WidgetSlot[]): void {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(value));
  }
  catch {
    // 저장이 막힌 환경(사생활 보호 모드). 이번 화면에서는 그대로 동작한다.
  }
}

function clear(userId: string): void {
  try {
    window.localStorage.removeItem(storageKey(userId));
  }
  catch {
    // 위와 같다.
  }
}

function isSlot(value: unknown): value is WidgetSlot {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.widgetKey === 'string' &&
    (candidate.size === 'sm' || candidate.size === 'wide') &&
    typeof candidate.visible === 'boolean'
  );
}
